import React, { useEffect, useState } from "react"
import { formatDateDataGrid } from "src/utils/format-date"
import PropTypes from 'prop-types'
import dayjs from "dayjs"
import { DATE_FORMAT, DATE_FORMAT_SHOW } from "src/utils/constants"
import DatalakeService from "src/services/datalake-service"
import ParameterPaginationTable from "src/components/table/parameter-pagination-table"

const ParameterMemoriaCalculoPUPARTable = (props) => {
    const datalakeService = new DatalakeService()
    const [data, setData] = useState([])
    const [pagination, setPagination] = useState({ count: 0, next: null, previous: null })

    useEffect(() => {
        loadData()
    }, [])

    const loadData = () => {
        props.loading(true)

        datalakeService.loadPaginationEndpoint(props.endpoint).then((response) => {
            props.loading(false)

            const { body } = response
            const { results, count, next, previous } = body
            const list = results.map((obj) => ({ ...obj, id: obj.ID }))

            setPagination({ count, next, previous })
            setData(list)
        }).catch((err) => {
            props.loading(false)
            props.callbackError(err)
        }).finally(() => {
            props.loading(false)
        })
    }

    const createRow = async () => {
        const body = {
            "CETIP": "N/A",
            "Indexador": "N/A",
            "DataReferencia": dayjs().format(DATE_FORMAT),
            "DataRentabilidadeInicio": dayjs().format(DATE_FORMAT),
            "DataEvento": dayjs().format(DATE_FORMAT),
            "DataLiquidacao": dayjs().format(DATE_FORMAT),
            "VN": 0,
            "VNA": 0,
            "DataIndexador": dayjs().format(DATE_FORMAT),
            "Defasagem": 0,
            "FatorIndexadorAcumulado": 0,
            "TaxaJuros": 0,
            "CalculoDias": "N/A",
            "JurosCapitalizacao": 0,
            "FatorJurosAcumulado": 0,
            "AtualizacaoMonetaria": 0,
            "FinanceiroJuros": 0,
            "IncorporacaoJuros": 0,
            "FinanceiroAmortizacao": 0,
            "FinanceiroAmortizacaoExtraordinaria": 0,
            "Pagamento": 0,
            "PUPAR": 0,
            "LOGERRO": ""
        }

        props.loading(true)

        return datalakeService.createTableEndpoint(props.endpoint, body).then(() => {
            props.loading(false)
            loadData(0, 25)
        }).catch((err) => {
            props.loading(false)

            console.error("Erro ao criar registro:")
            console.error("Mensagem:", err?.message)
            console.error("Response:", err?.response)
            console.error("Response data:", err?.response?.data)
            console.error("Status:", err?.response?.status)

            throw err
        })
    }

    const updateRow = async (newRow) => {
        const {
            ID,
            CETIP,
            Indexador,
            DataReferencia,
            DataRentabilidadeInicio,
            DataEvento,
            DataLiquidacao,
            VN,
            VNA,
            DataIndexador,
            Defasagem,
            FatorIndexadorAcumulado,
            TaxaJuros,
            CalculoDias,
            JurosCapitalizacao,
            FatorJurosAcumulado,
            AtualizacaoMonetaria,
            FinanceiroJuros,
            IncorporacaoJuros,
            FinanceiroAmortizacao,
            FinanceiroAmortizacaoExtraordinaria,
            Pagamento,
            PUPAR,
            LOGERRO
        } = newRow

        const body = {
            "CETIP": CETIP,
            "Indexador": Indexador,
            "DataReferencia": DataReferencia,
            "DataRentabilidadeInicio": DataRentabilidadeInicio,
            "DataEvento": DataEvento,
            "DataLiquidacao": DataLiquidacao,
            "VN": VN,
            "VNA": VNA,
            "DataIndexador": DataIndexador,
            "Defasagem": Defasagem,
            "FatorIndexadorAcumulado": FatorIndexadorAcumulado,
            "TaxaJuros": TaxaJuros,
            "CalculoDias": CalculoDias,
            "JurosCapitalizacao": JurosCapitalizacao,
            "FatorJurosAcumulado": FatorJurosAcumulado,
            "AtualizacaoMonetaria": AtualizacaoMonetaria,
            "FinanceiroJuros": FinanceiroJuros,
            "IncorporacaoJuros": IncorporacaoJuros,
            "FinanceiroAmortizacao": FinanceiroAmortizacao,
            "FinanceiroAmortizacaoExtraordinaria": FinanceiroAmortizacaoExtraordinaria,
            "Pagamento": Pagamento,
            "PUPAR": PUPAR,
            "LOGERRO": LOGERRO
        }

        props.loading(true)

        return datalakeService.updateTableEndpoint(props.endpoint, ID, body).then(() => {
            props.loading(false)
        }).catch((err) => {
            props.loading(false)
            throw err
        })
    }

    const onFilter = async (filterList, limit, offset) => {
        const query = filterList
            .map(obj => `${encodeURIComponent(obj.field)}=${encodeURIComponent(obj.value)}`)
            .join('&')

        props.loading(true)

        datalakeService.loadPaginationEndpoint(props.endpoint, query, limit, offset).then((response) => {
            props.loading(false)

            const { body } = response
            const { results, count, next, previous } = body
            const list = results.map((obj) => ({ ...obj, id: obj.ID }))

            setPagination({ count, next, previous })
            setData(list)
        })
    }

    const changePage = async (endpoint) => {
        props.loading(true)

        datalakeService.paginate(endpoint).then((response) => {
            props.loading(false)

            const { body } = response
            const { results, count, previous, next } = body
            const list = results.map((obj) => ({ ...obj, id: obj.ID }))

            setPagination({ count, next, previous })
            setData(list)
        })
    }

    const columns = [
        { field: 'ID', headerName: '#', disableColumnMenu: true, disableFilter: true, flex: true, minWidth: 80 },

        { field: 'CETIP', headerName: 'CETIP', flex: 1, minWidth: 150, editable: true },
        { field: 'Indexador', headerName: 'Indexador', flex: 1, minWidth: 150, editable: true },

        {
            field: 'DataReferencia',
            headerName: 'DataReferencia',
            flex: 1,
            minWidth: 150,
            typeComponent: 'date',
            editable: true,
            renderCell: (params) => params.value ? dayjs(params.value).format(DATE_FORMAT_SHOW) : ''
        },
        {
            field: 'DataRentabilidadeInicio',
            headerName: 'DataRentabilidadeInicio',
            flex: 1,
            minWidth: 180,
            typeComponent: 'date',
            editable: true,
            renderCell: (params) => params.value ? dayjs(params.value).format(DATE_FORMAT_SHOW) : ''
        },
        {
            field: 'DataEvento',
            headerName: 'DataEvento',
            flex: 1,
            minWidth: 150,
            typeComponent: 'date',
            editable: true,
            renderCell: (params) => params.value ? dayjs(params.value).format(DATE_FORMAT_SHOW) : ''
        },
        {
            field: 'DataLiquidacao',
            headerName: 'DataLiquidacao',
            flex: 1,
            minWidth: 150,
            typeComponent: 'date',
            editable: true,
            renderCell: (params) => params.value ? dayjs(params.value).format(DATE_FORMAT_SHOW) : ''
        },

        { field: 'VN', headerName: 'VN', flex: 1, minWidth: 150, typeComponent: 'number', type: 'number', editable: true },
        { field: 'VNA', headerName: 'VNA', flex: 1, minWidth: 150, typeComponent: 'number', type: 'number', editable: true },

        {
            field: 'DataIndexador',
            headerName: 'DataIndexador',
            flex: 1,
            minWidth: 150,
            typeComponent: 'date',
            editable: true,
            renderCell: (params) => params.value ? dayjs(params.value).format(DATE_FORMAT_SHOW) : ''
        },

        { field: 'Defasagem', headerName: 'Defasagem', flex: 1, minWidth: 150, typeComponent: 'number', type: 'number', editable: true },
        { field: 'FatorIndexadorAcumulado', headerName: 'FatorIndexadorAcumulado', flex: 1, minWidth: 200, typeComponent: 'number', type: 'number', editable: true },
        { field: 'TaxaJuros', headerName: 'TaxaJuros', flex: 1, minWidth: 150, typeComponent: 'number', type: 'number', editable: true },
        { field: 'CalculoDias', headerName: 'CalculoDias', flex: 1, minWidth: 150, editable: true },
        { field: 'JurosCapitalizacao', headerName: 'JurosCapitalizacao', flex: 1, minWidth: 180, editable: true },

        { field: 'FatorJurosAcumulado', headerName: 'FatorJurosAcumulado', flex: 1, minWidth: 200, typeComponent: 'number', type: 'number', editable: true },
        { field: 'AtualizacaoMonetaria', headerName: 'AtualizacaoMonetaria', flex: 1, minWidth: 180, typeComponent: 'number', type: 'number', editable: true },
        { field: 'FinanceiroJuros', headerName: 'FinanceiroJuros', flex: 1, minWidth: 180, typeComponent: 'number', type: 'number', editable: true },
        { field: 'IncorporacaoJuros', headerName: 'IncorporacaoJuros', flex: 1, minWidth: 180, typeComponent: 'number', type: 'number', editable: true },
        { field: 'FinanceiroAmortizacao', headerName: 'FinanceiroAmortizacao', flex: 1, minWidth: 200, typeComponent: 'number', type: 'number', editable: true },
        { field: 'FinanceiroAmortizacaoExtraordinaria', headerName: 'FinanceiroAmortizacaoExtraordinaria', flex: 1, minWidth: 250, typeComponent: 'number', type: 'number', editable: true },
        { field: 'Pagamento', headerName: 'Pagamento', flex: 1, minWidth: 150, typeComponent: 'number', type: 'number', editable: true },
        { field: 'PUPAR', headerName: 'PUPAR', flex: 1, minWidth: 150, typeComponent: 'number', type: 'number', editable: true },

        { field: 'LOGERRO', headerName: 'LOGERRO', flex: 1, minWidth: 250, editable: true },

        {
            field: 'CriadoEm',
            headerName: 'Data de Criação',
            flex: true,
            minWidth: 150,
            disableColumnMenu: true,
            disableFilter: true,
            renderCell: (params) => formatDateDataGrid(params)
        },
        {
            field: 'AtualizadoEm',
            headerName: 'Data de Atualização',
            flex: true,
            minWidth: 150,
            disableColumnMenu: true,
            disableFilter: true,
            renderCell: (params) => formatDateDataGrid(params)
        }
    ]

    return (
        <div>
            <ParameterPaginationTable
                columns={columns}
                data={data}
                setData={setData}
                createRow={createRow}
                updateRow={updateRow}
                pagination={pagination}
                onChangePage={changePage}
                filterFunction={onFilter}
                customFilter={true}
            />
        </div>
    )
}

ParameterMemoriaCalculoPUPARTable.propTypes = {
    loading: PropTypes.func,
    endpoint: PropTypes.string,
    callbackError: PropTypes.func
}

export default ParameterMemoriaCalculoPUPARTable